
// File: src/components/Invoices/OperationalHeader.jsx

import React from "react";
import {
  ArrowLeft,
  FileText,
  Building2,
  ClipboardList,
  ShieldCheck,
  AlertTriangle,
  CheckCircle,
  Clock,
} from "lucide-react";

function statusClass(status) {
  const safe = String(status || "").toLowerCase();

  if (safe === "paid") return "border-green-400/30 bg-green-500/10 text-green-300";
  if (safe === "approved") return "border-blue-400/30 bg-blue-500/10 text-blue-300";
  if (safe === "rejected") return "border-red-400/30 bg-red-500/10 text-red-300";
  if (safe.includes("pending")) return "border-amber-400/30 bg-amber-500/10 text-amber-300";

  return "border-white/10 bg-white/5 text-gray-300";
}

function riskMeta(risk) {
  const safe = String(risk || "low").toLowerCase();

  if (safe === "high") {
    return {
      label: "High Risk",
      icon: AlertTriangle,
      className: "border-red-400/30 bg-red-500/10 text-red-300",
    };
  }

  if (safe === "medium") {
    return {
      label: "Medium Risk",
      icon: Clock,
      className: "border-amber-400/30 bg-amber-500/10 text-amber-300",
    };
  }

  return {
    label: "Low Risk",
    icon: CheckCircle,
    className: "border-green-400/30 bg-green-500/10 text-green-300",
  };
}

export default function OperationalHeader({
  invoice,
  risk = "low",
  confidence = null,
  onBack,
  onReview,
  onViewInvoice,
}) {
  const safeStatus = invoice?.status || invoice?.payment_status || "unknown";
  const invoiceNumber = invoice?.invoice_number || `#${invoice?.invoice_id || "—"}`;
  const vendorName = invoice?.vendor_name || "Unknown vendor";
  const poNumber = invoice?.po_number || invoice?.po_id || "—";
  const amount = Number(invoice?.amount_due || invoice?.amount || 0);
  const lastDate =
    invoice?.paid_at ||
    invoice?.approved_at ||
    invoice?.rejected_at ||
    invoice?.uploaded_at ||
    invoice?.created_at ||
    null;

  const RiskIcon = riskMeta(risk).icon;
  const riskInfo = riskMeta(risk);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="mb-4 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Operational Intelligence
            </button>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5">
              <FileText className="h-5 w-5 text-blue-300" />
            </div>

            <div>
              <h1 className="text-2xl font-bold">🧠 Operational Intelligence</h1>
              <div className="mt-1 text-sm text-gray-400">
                Invoice{" "}
                <span className="font-mono text-amber-300">{invoiceNumber}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {onReview && (
            <button
              type="button"
              onClick={onReview}
              className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold hover:bg-white/10"
            >
              Review
            </button>
          )}

          {onViewInvoice && (
            <button
              type="button"
              onClick={onViewInvoice}
              className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold hover:bg-blue-500"
            >
              View Invoice
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Building2 className="h-4 w-4" />
            Supplier
          </div>
          <div className="mt-1 font-semibold">{vendorName}</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <ClipboardList className="h-4 w-4" />
            Purchase Order
          </div>
          <div className="mt-1 font-mono font-semibold text-gray-200">{poNumber}</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <ShieldCheck className="h-4 w-4" />
            Status
          </div>
          <div
            className={`mt-2 inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusClass(
              safeStatus
            )}`}
          >
            {String(safeStatus).toUpperCase()}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
          <div className="text-xs text-gray-400">Amount</div>
          <div className="mt-1 text-lg font-bold">
            {new Intl.NumberFormat("en-GB", {
              style: "currency",
              currency: "GBP",
            }).format(amount)}
          </div>
        </div>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${riskInfo.className}`}>
          <RiskIcon className="h-4 w-4" />
          {riskInfo.label}
        </span>

        {confidence !== null && confidence !== undefined && (
          <span className="inline-flex rounded-full border border-purple-400/30 bg-purple-500/10 px-3 py-1 text-xs font-semibold text-purple-300">
            AI Confidence {Number(confidence).toFixed(0)}%
          </span>
        )}

        {lastDate && (
          <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-gray-300">
            Last activity {new Date(lastDate).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}